/**
 * RecordingDetail Modal (T122 + T123)
 * Video player, transcript, download, and access management
 */

import { useState, useRef, useCallback } from 'react';
import {
  X,
  Download,
  Users,
  Clock,
  HardDrive,
  Calendar,
  Video,
  FileText,
  UserPlus,
  UserMinus,
  Volume2,
} from 'lucide-react';
import {
  useRecording,
  useStreamUrl,
  useDownloadUrl,
  useGrantAccess,
  useRevokeAccess,
  formatDuration,
  formatFileSize,
  type AccessGrant,
} from '../../hooks/useRecordings';
import TranscriptViewer from './TranscriptViewer';

interface Props {
  recordingId: string;
  onClose: () => void;
}

const STATUS_BADGE: Record<string, string> = {
  PROCESSING: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  AVAILABLE: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  ARCHIVED: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  UNAVAILABLE: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  ERROR: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export default function RecordingDetail({ recordingId, onClose }: Props) {
  const { data: recording, isLoading, error } = useRecording(recordingId);
  const { data: streamData } = useStreamUrl(recording?.hasVideo ? recordingId : null);
  const downloadUrl = useDownloadUrl();
  const grantAccess = useGrantAccess();
  const revokeAccess = useRevokeAccess();

  const [activeTab, setActiveTab] = useState<'player' | 'transcript' | 'access'>('player');
  const [grantCustomerId, setGrantCustomerId] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleDownload = useCallback(
    async (type: 'video' | 'audio' | 'transcript') => {
      const result = await downloadUrl.mutateAsync({ recordingId, type });
      window.open(result.downloadUrl, '_blank');
    },
    [recordingId, downloadUrl]
  );

  const handleSeekToTimestamp = useCallback((seconds: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = seconds;
      videoRef.current.play();
      setActiveTab('player');
    }
  }, []);

  const handleGrantAccess = useCallback(async () => {
    if (!grantCustomerId.trim()) {
      return;
    }
    await grantAccess.mutateAsync({
      recordingId,
      customerId: grantCustomerId.trim(),
      notify: true,
    });
    setGrantCustomerId('');
  }, [recordingId, grantCustomerId, grantAccess]);

  const handleRevokeAccess = useCallback(
    async (accessId: string) => {
      await revokeAccess.mutateAsync({ recordingId, accessId });
    },
    [recordingId, revokeAccess]
  );

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="claude-card w-full max-w-4xl p-8">
          <div className="flex items-center justify-center">
            <div className="claude-spinner mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !recording) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="claude-card w-full max-w-4xl p-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Error</h2>
            <button onClick={onClose} className="p-1 hover:bg-muted rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-destructive">Failed to load recording details.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'player' as const, label: 'Player', icon: Video },
    {
      key: 'transcript' as const,
      label: 'Transcript',
      icon: FileText,
      disabled: !recording.hasTranscript,
    },
    { key: 'access' as const, label: 'Access', icon: Users },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="claude-card w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-start justify-between shrink-0">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold tracking-tight">
              {recording.appointment?.title || 'Recording'}
            </h2>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              {recording.customer && (
                <span className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  {recording.customer.name}
                </span>
              )}
              {recording.recordedAt && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(recording.recordedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              )}
              <span
                className={`claude-badge text-xs ${STATUS_BADGE[recording.processingStatus] || ''}`}
              >
                {recording.processingStatus}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-6 shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              disabled={tab.disabled}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors
                ${
                  activeTab === tab.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }
                ${tab.disabled ? 'opacity-40 cursor-not-allowed' : ''}
              `}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Player Tab */}
          {activeTab === 'player' && (
            <div className="space-y-4">
              {/* Video player */}
              {recording.hasVideo && streamData?.streamUrl ? (
                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                  <video
                    ref={videoRef}
                    src={streamData.streamUrl}
                    controls
                    className="w-full h-full"
                    preload="metadata"
                  >
                    Your browser does not support the video element.
                  </video>
                </div>
              ) : (
                <div className="aspect-video bg-muted/30 rounded-lg flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <Video className="w-12 h-12 mx-auto mb-2 opacity-40" />
                    <p>
                      {recording.processingStatus === 'PROCESSING'
                        ? 'Video is processing...'
                        : 'No video available'}
                    </p>
                  </div>
                </div>
              )}

              {/* Info bar */}
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {formatDuration(recording.duration)}
                </span>
                <span className="flex items-center gap-1.5">
                  <HardDrive className="w-4 h-4" />
                  {formatFileSize(recording.fileSize)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Users className="w-4 h-4" />
                  {recording.accessGrants?.length || 0} access grants
                </span>
              </div>

              {/* Download buttons */}
              <div className="flex gap-2 flex-wrap">
                {recording.hasVideo && (
                  <button
                    onClick={() => handleDownload('video')}
                    disabled={downloadUrl.isPending}
                    className="claude-button claude-button-secondary text-sm flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Video
                  </button>
                )}
                {recording.hasAudio && (
                  <button
                    onClick={() => handleDownload('audio')}
                    disabled={downloadUrl.isPending}
                    className="claude-button claude-button-secondary text-sm flex items-center gap-2"
                  >
                    <Volume2 className="w-4 h-4" />
                    Download Audio
                  </button>
                )}
                {recording.hasTranscript && (
                  <button
                    onClick={() => handleDownload('transcript')}
                    disabled={downloadUrl.isPending}
                    className="claude-button claude-button-secondary text-sm flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Download Transcript
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Transcript Tab */}
          {activeTab === 'transcript' && recording.transcript && (
            <TranscriptViewer transcript={recording.transcript} onSeek={handleSeekToTimestamp} />
          )}

          {/* Access Tab (T123) */}
          {activeTab === 'access' && (
            <div className="space-y-6">
              {/* Grant access form */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  Grant Access to Customer
                </h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Customer ID"
                    value={grantCustomerId}
                    onChange={(e) => setGrantCustomerId(e.target.value)}
                    className="claude-input flex-1 text-sm"
                  />
                  <button
                    onClick={handleGrantAccess}
                    disabled={!grantCustomerId.trim() || grantAccess.isPending}
                    className="claude-button claude-button-primary text-sm"
                  >
                    {grantAccess.isPending ? 'Granting...' : 'Grant'}
                  </button>
                </div>
                {grantAccess.isError && (
                  <p className="text-sm text-destructive">
                    Failed to grant access. Check customer ID.
                  </p>
                )}
              </div>

              {/* Current access list */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Current Access Grants</h3>
                {recording.accessGrants && recording.accessGrants.length > 0 ? (
                  <div className="space-y-2">
                    {recording.accessGrants.map((grant: AccessGrant) => (
                      <div
                        key={grant.id}
                        className="flex items-center justify-between p-3 bg-muted/20 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-sm">{grant.customerName}</p>
                          <p className="text-xs text-muted-foreground">{grant.customerEmail}</p>
                          <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                            <span>Granted: {new Date(grant.grantedAt).toLocaleDateString()}</span>
                            <span>Views: {grant.accessCount}</span>
                            {grant.expiresAt && (
                              <span className={grant.isExpired ? 'text-destructive' : ''}>
                                Expires: {new Date(grant.expiresAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRevokeAccess(grant.id)}
                          disabled={revokeAccess.isPending}
                          className="claude-button claude-button-secondary text-sm text-destructive hover:bg-destructive/10"
                        >
                          <UserMinus className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No access grants yet.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
